import { useTranslation } from 'react-i18next'
import {
	useInputSliderPercentageAdapter,
	WidgetInputGradient,
	WidgetInputSliderField,
	WidgetLabel,
	WidgetSelectDropdown,
} from '../../../../rapid-ui'
import { MetaOverlayConfiguration } from '../../../../views'
import type { SimulatorType } from '../../../tracking'
import { trackOverlayEdited } from '../../../tracking'
import { EditorWidgetColorField } from '../editor-widget-color-field'
import { fillBlendModeOptions, getFillOptions, getMaskOptions, gradientOptions } from './options'

export function ProOverlayEditor({ overlay, onChange, simulatorType }: {
	overlay: MetaOverlayConfiguration
	onChange: (overlay: MetaOverlayConfiguration) => void
	simulatorType: SimulatorType
}) {
	const { t } = useTranslation('common')

	return (
		<>
			<div className="grid gap-2 auto-cols-fr grid-flow-col">
				<WidgetSelectDropdown
					options={getFillOptions(t)}
					value={overlay.fillMode}
					onChange={(value) => {
						trackOverlayEdited('fill_mode', simulatorType)
						onChange({ ...overlay, fillMode: value })
					}}
					label={t('editor.overlay.fillMode')}
				/>
				{overlay.fillMode === 'flat' && (
					<>
						<EditorWidgetColorField
							value={overlay.fillFlatColor}
							onChange={(value) => {
								trackOverlayEdited('fill_color', simulatorType)
								onChange({ ...overlay, fillFlatColor: value })
							}}
							label={t('editor.overlay.fillColor')}
						/>
						<WidgetSelectDropdown
							options={fillBlendModeOptions}
							value={overlay.fillBlendMode}
							onChange={(value) => {
								trackOverlayEdited('fill_blend_mode', simulatorType)
								onChange({ ...overlay, fillBlendMode: value })
							}}
							label={t('editor.overlay.fillBlendMode')}
						/>
					</>
				)}
			</div>
			{overlay.fillMode !== 'none' && (
				<OverlayEditorMaskOptions overlay={overlay} onChange={onChange} simulatorType={simulatorType} />
			)}
		</>
	)
}

function OverlayEditorMaskOptions({ overlay, onChange, simulatorType }: {
	overlay: MetaOverlayConfiguration
	onChange: (overlay: MetaOverlayConfiguration) => void
	simulatorType: SimulatorType
}) {
	const { t } = useTranslation('common')
	const flatOpacitySlider = useInputSliderPercentageAdapter({
		value: overlay.maskFlatOpacity,
		onChange: (value: number) => {
			trackOverlayEdited('mask_opacity', simulatorType)
			onChange({ ...overlay, maskFlatOpacity: value })
		},
	})

	const gradientOpacity1Slider = useInputSliderPercentageAdapter({
		value: overlay.maskGradientOpacity1,
		onChange: (value: number) => {
			trackOverlayEdited('gradient_opacity_left', simulatorType)
			onChange({ ...overlay, maskGradientOpacity1: value })
		},
	})

	const gradientOpacity2Slider = useInputSliderPercentageAdapter({
		value: overlay.maskGradientOpacity2,
		onChange: (value: number) => {
			trackOverlayEdited('gradient_opacity_right', simulatorType)
			onChange({ ...overlay, maskGradientOpacity2: value })
		},
	})

	return (
		<>
			<div className="text-xs text-grey-500 text-left mt-2">{t('editor.overlay.maskOptions')}</div>
			<div className="grid grid-cols-[1fr_1fr] gap-2">
				<WidgetSelectDropdown
					options={getMaskOptions(t)}
					value={overlay.maskMode}
					onChange={(value) => {
						trackOverlayEdited('mask_mode', simulatorType)
						onChange({ ...overlay, maskMode: value })
					}}
					label={t('editor.overlay.maskMode')}
				/>

				{overlay.maskMode === 'flat' && (
					<WidgetInputSliderField {...flatOpacitySlider} label={t('editor.overlay.maskOpacity')} />
				)}

				{overlay.maskMode === 'gradient' && (
					<WidgetSelectDropdown
						options={gradientOptions}
						value={overlay.maskGradientDirection}
						onChange={(value) => {
							trackOverlayEdited('mask_gradient_direction', simulatorType)
							onChange({ ...overlay, maskGradientDirection: value })
						}}
						label={t('editor.overlay.gradientDirection')}
					/>
				)}
			</div>

			{overlay.maskMode === 'gradient' && (
				<WidgetInputGradient
					leftOffset={overlay.maskGradientStop1}
					rightOffset={overlay.maskGradientStop2}
					leftColor={`rgba(0, 0, 0, ${overlay.maskGradientOpacity1})`}
					rightColor={`rgba(0, 0, 0, ${overlay.maskGradientOpacity2})`}
					onChange={(leftOffset, rightOffset) => {
						trackOverlayEdited('gradient_handle', simulatorType)
						onChange({
							...overlay,
							maskGradientStop1: leftOffset,
							maskGradientStop2: rightOffset,
						})
					}}
					leftChildren={
						<div className="text-xs p-2 bg-grey-50 ring-1 ring-black/5 mt-1 rounded shadow-sm">
							<WidgetLabel label={t('editor.overlay.maskOpacity')} position="below">
								<WidgetInputSliderField {...gradientOpacity1Slider} />
							</WidgetLabel>
						</div>
					}
					rightChildren={
						<div className="text-xs p-2 bg-grey-50 ring-1 ring-black/5 mt-1 rounded shadow-sm">
							<WidgetLabel label={t('editor.overlay.maskOpacity')} position="below">
								<WidgetInputSliderField {...gradientOpacity2Slider} />
							</WidgetLabel>
						</div>
					}
				/>
			)}
		</>
	)
}
